import {createEntityAdapter, createSelector, createSlice, SerializedError} from "@reduxjs/toolkit";
import {RootState} from "../../../redux/store";
import {
    addWorkerServiceThunk,
    createWorkerThunk,
    getWorkerThunk,
    listWorkersThunk,
    removeWorkerServiceThunk,
    updateWorkerStatusThunk,
    updateWorkerThunk
} from "./workers.thunk";
import {FeWorker} from "./types";
import {selectAllOrders} from "../../orders/redux/orders.slice";
import {selectAllAvailabilities} from "../../availibilities/redux/availabilities.slice";
import {WorkerDay} from "../../orders/redux/types";
import {isSameDay, isSameMonth, setDate} from "date-fns";
import _ from "lodash";

const workerAdapter = createEntityAdapter<FeWorker>();

type ErrorMap = {
    [id: string]: SerializedError | undefined;
};


const workersSlice = createSlice({
    name: 'workers',
    initialState: workerAdapter.getInitialState({errors: {} as ErrorMap}),
    reducers: {
        clearAllWorkerErrors(state) {
            state.errors = {};
        }
    },
    extraReducers: builder =>
        builder
            .addCase(listWorkersThunk.fulfilled, (state, {payload}) => {
                state.errors.list = undefined;
                workerAdapter.upsertMany(state, payload);
            })
            .addCase(listWorkersThunk.rejected, (state, action) => {
                state.errors.list = action.error;
            })
            .addCase(createWorkerThunk.fulfilled, (state, action) => {
                workerAdapter.upsertOne(state, action.payload);
                state.errors[action.payload.id] = undefined;
            })
            .addCase(createWorkerThunk.rejected, (state, {error}) => {
                state.errors.create = error;
            })
            .addCase(updateWorkerThunk.fulfilled, (state, action) => {
                const {id, ...changes} = action.payload;
                workerAdapter.updateOne(state, {id, changes});
                state.errors[action.payload.id] = undefined;
            })
            .addCase(updateWorkerThunk.rejected, (state, action) => {
                state.errors[action.meta.arg.id] = action.error;
            })
            .addCase(updateWorkerStatusThunk.fulfilled, (state, action) => {
                const {id, ...changes} = action.payload;
                workerAdapter.updateOne(state, {id, changes});
                state.errors[action.payload.id] = undefined;
            })
            .addCase(addWorkerServiceThunk.fulfilled, (state, {payload}) => {
                const worker = state.entities[payload.workerId];
                if (worker) {
                    workerAdapter.updateOne(state, {
                        id: payload.workerId,
                        changes: {
                            servicesIds: [...worker.servicesIds, payload.serviceId],
                            updatedAt: new Date().getTime()
                        }
                    })
                }
            })
            .addCase(removeWorkerServiceThunk.fulfilled, (state, {payload}) => {
                const worker = state.entities[payload.workerId];
                if (worker) {
                    workerAdapter.updateOne(state, {
                        id: payload.workerId,
                        changes: {
                            servicesIds: [...worker.servicesIds.filter((s) => s !== payload.serviceId)],
                            updatedAt: new Date().getTime()
                        }
                    })
                }
            })
            .addCase(updateWorkerStatusThunk.rejected, (state, action) => {
                state.errors[action.meta.arg.id] = action.error;
            })
            .addCase(getWorkerThunk.fulfilled, (state, action) => {
                workerAdapter.upsertOne(state, action.payload);
                state.errors[action.payload.id] = undefined;
            })
            .addCase(getWorkerThunk.rejected, (state, action) => {
                state.errors[action.meta.arg.id] = action.error;
            })
});

export const {
    selectById: selectWorkerById,
    selectIds: selectWorkerIds,
    selectEntities: selectWorkerEntities,
    selectAll: selectAllWorkers,
    selectTotal: selectTotalWorkers,
} = workerAdapter.getSelectors((state: RootState) => state.workers);

export const {clearAllWorkerErrors} = workersSlice.actions;

interface WorkerDaysSelectConfig {
    workerId: string,
    selectedMonth: Date
}

export const makeSelectWorkerDays = () => {
    return selectWorkerDays;
}

export default workersSlice.reducer;

export const selectWorkerDays = createSelector(
    (_state: RootState, config: WorkerDaysSelectConfig) => config,
    selectAllOrders,
    selectAllAvailabilities,
    (_state, props: { daysInMonth: number }) => _.fill(Array(props.daysInMonth), {
        availability: null,
        orders: []
    } as WorkerDay),
    (config, allOrders, allAvailabilities, emptyDays) => {

        const workerAvailabilities = allAvailabilities
            .filter((a) =>
                a.workerId === config.workerId &&
                isSameMonth(config.selectedMonth, a.from)
            );

        const workerOrders = allOrders
            .filter(o =>
                o.workerId === config.workerId &&
                isSameMonth(o.estimatedFrom, config.selectedMonth)
            );

        const workerDays: WorkerDay[] = emptyDays.map((eD, edI) => {

            const currentDay = setDate(config.selectedMonth, edI + 1);

            return ({
                orders: workerOrders.filter(o => isSameDay(currentDay, o.estimatedFrom)),
                availability: workerAvailabilities.find(a => isSameDay(a.from, currentDay)) ?? null
            })
        });

        return {workerDays, workerOrders, workerAvailabilities}
    }
);
